import {
  eventDispatcher,
  eventListener,
  ISubscribeTheme,
  TAL_THEME_CHANGE,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import { OriginThemeMode } from "@origin-digital/ods-types";
import { useEffect, useRef, useState } from "react";

interface ThemeChangeEvent extends MessageEvent {
  data: OriginThemeMode;
}

// Extend the Document interface to include the custom event
declare global {
  interface DocumentEventMap {
    [TAL_THEME_CHANGE]: ThemeChangeEvent;
  }
}

export const isValidTheme = (theme: string): theme is OriginThemeMode => {
  return theme === "light" || theme === "dark";
};

export const DEFAULT_THEME_MODE: OriginThemeMode = "light";

/**
 * Subscribe and unsubscribe to SUBSCRIBE_THEME events, and handle fetching initial theme.
 */
export const useThemeListener = () => {
  const subscribersList = useRef<
    ((theme: OriginThemeMode, isPageload: boolean) => void)[]
  >([]);
  const initialFetchThemeRef = useRef<OriginThemeMode>(DEFAULT_THEME_MODE);
  const [currentThemeMode, setCurrentThemeMode] =
    useState<OriginThemeMode>(DEFAULT_THEME_MODE);

  const publishToSubscribers = (theme: OriginThemeMode, isPageload = false) => {
    setCurrentThemeMode(theme);
    subscribersList.current.forEach((subscriber) => {
      subscriber(theme, isPageload);
    });
  };

  useEffect(() => {
    // ============================================================================
    // Create subscribers list
    // ============================================================================
    const handleSubscribeTheme = (event: ISubscribeTheme) => {
      const { subscriber } = event.payload;
      // notify subscriber right away - this is important in case the microsite registers after FETCH_THEME resolves
      subscriber(initialFetchThemeRef.current, true);
      subscribersList.current.push(subscriber);
      const unsubscribe = () => {
        const index = subscribersList.current.indexOf(subscriber);
        if (index > -1) {
          subscribersList.current.splice(index, 1);
        }
      };

      return unsubscribe;
    };
    const unsubscribeTheme = eventListener.addListener(
      TOPICS.SUBSCRIBE_THEME,
      handleSubscribeTheme
    );

    // ============================================================================
    // Add listener to notify subscribers on theme change
    // ============================================================================
    const handleThemeChange = (event: ThemeChangeEvent) => {
      const newTheme = event?.data;
      if (isValidTheme(newTheme)) {
        publishToSubscribers(newTheme);
      }
    };
    document.addEventListener(TAL_THEME_CHANGE, handleThemeChange);

    // ============================================================================
    // Fetch initial theme and notify subscribers
    // ============================================================================
    const fetchTheme = async () => {
      // fetch current theme via mesh
      const result = await eventDispatcher.dispatch({
        topic: TOPICS.FETCH_THEME,
        payload: {},
      });

      const initialTheme = result?.theme;
      if (isValidTheme(initialTheme)) {
        initialFetchThemeRef.current = initialTheme;
        publishToSubscribers(initialTheme, true);
      }
    };
    fetchTheme();

    return () => {
      unsubscribeTheme?.();
      document.removeEventListener(TAL_THEME_CHANGE, handleThemeChange);
    };
  }, []);

  return currentThemeMode;
};

export const ThemeListener = () => {
  useThemeListener();

  return null;
};
